// © Microsoft Corporation. All rights reserved.

import { DesignSystem, accentFillRest, accentForegroundRest, isDarkMode, neutralFillStealthActive, neutralForegroundHint, neutralForegroundRest, neutralLayerCard } from "@microsoft/fast-components-styles-msft";
import { enums, getEmojiStylesByWidth, reset } from "@msnews/social-utils";

import { applyLocalizedProperty } from "@microsoft/fast-jss-utilities";

const sidePadding = 20;
const avatarSize = "28px";
const width = 466;

const scrollGradientStyle = (config) => {
    return {
        content: "''",
        width: "100%",
        height: "20px",
        position: "absolute",
        "background-repeat": "no-repeat",
        "z-index": "1",
        left: "0",
        right: "0",
        background: isDarkMode(config) ?  "linear-gradient(180deg, rgba(66, 66, 66, 0), rgba(66, 66, 66, 1))" : "linear-gradient(180deg, rgba(255, 255, 255, 0), #FFFFFF)"
    };
};

export const ReactionBreakdownStyles = (config: DesignSystem) => ({
    reactionBreakdownContainer: {
        ...reset,
        "box-sizing": "content-box !important",
        display: "block !important",
        margin: "0 auto",

        ["&."+enums.responsiveLevels.MobilePopup]: {
            display: "flex !important",
            "max-width": "100vw",
            "overflow-x": "hidden",

            "& .cwt-reaction-breakdown-inner": {
                width: "100vw",

                "& .cwt-tab-content": {
                    height: "unset",
                    "min-height": "270px",
                    "max-height": `${screen.height*.9 - 120}px`
                }
            }
        },

        "& div.emoji-icon": {
            ...getEmojiStylesByWidth(16),
            transition: "none"
        },

        "& .cwt-reaction-breakdown-inner": {
            "box-sizing": "content-box !important",
            display: "block !important",
            width: `${width - 2*sidePadding}px`,
            "max-width": "100vw",
            margin: "0 auto",
            padding: "0",
            background: neutralLayerCard,
            "overflow-x": "hidden",

            "& .cwt-reaction-breakdown-header": {
                position: "sticky",
                top: "0",
                "z-index": "5",
                "padding-top": "20px",
                background: neutralLayerCard
            },

            "& .cwt-title": {
                color: neutralForegroundRest,
                margin: "0 0 16px",
                "font-size": "16px !important",
                "font-weight": "bold",
                "text-align": "start",
                padding: `0 ${sidePadding}px`
            },

            "& .cwt-tabs": {
                padding: `0 ${sidePadding}px`,
                "align-items": "flex-start",
                "& .cwt-tab-item": {
                    position: "relative",
                    [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "4px",
                    "border-radius": "8px 8px 0 0",
                    "min-height": "26px",

                    "&.active > .cwt-icon > .cwt-icon-label": {
                        color: isDarkMode(config) ? "#5CB6FF" : accentFillRest
                    },
                    "&.active:after": {
                        position: "absolute",
                        content: "' '",
                        height: "3px",
                        width: "100%",
                        bottom: "0",
                        [applyLocalizedProperty("left", "right", config.direction)]: "0",
                        background: accentForegroundRest
                    },

                    "&.cwt-link:hover": {
                        "text-decoration": "none"
                    }
                }
            },

            "& .cwt-h-seperator": {
                height: "1px",
                margin: "0",
                background: "rgba(0, 0, 0, 0.08)",
                border: "none"
            },

            "& .cwt-badge": {
                "font-size": "14px",
                color: isDarkMode(config) ? "#D1D1D1" : "#4D4D4D"
            },

            "& .cwt-tab-content": {
                display: "block !important",
                height: "280px",
                margin: `0 -${sidePadding}px`,
                "overflow-y": "auto",
                "&": {
                    // This is for cross-browser compatibility of scrollbar positioning,
                    // overlay is not supported on all browsers
                    "overflow-y": "overlay"
                },

                // Scrollbar
                "scrollbar-width": "thin",
                "scrollbar-color": "rgba(0, 0, 0, 0.3) transparent",
                "&::-webkit-scrollbar": {
                    width: "6px"
                },
                "&::-webkit-scrollbar-track": {
                    background: "transparent"
                },
                "&::-webkit-scrollbar-thumb": {
                    "background-color": "rgba(0, 0, 0, 0.3)",
                    "border-radius": "6px"
                },

                "&:before": {
                    ...scrollGradientStyle(config),
                    transform: "translateY(-5px) scaleY(-.75)"
                },

                "&:after": {
                    ...scrollGradientStyle(config),
                    bottom: "0"
                }
            },

            "& .cwt-icon": {
                "& div.emoji-icon": {
                    ...getEmojiStylesByWidth(16)
                },
                "& .cwt-icon-label": {
                    "margin-top": "-1.25px",
                    "font-size": "14px"
                }
            },

            "& .cwt-reacting-users": {
                display: "flex",
                "flex-direction": "column",
                padding: `0 ${sidePadding}px`,
                margin: "8px 0 32px",

                "& li": {
                    display: "flex",
                    "align-items": "center",
                    "justify-content": "space-between",
                    "font-size": "14px",
                    padding: `0 ${sidePadding}px`,
                    "min-height": "42px",
                    "max-height": "60px",

                    "&:hover": {
                        background: neutralFillStealthActive
                    },

                    "& .cwt-avatar": {
                        height: avatarSize,
                        "min-width": avatarSize,
                        "max-width": avatarSize
                    },

                    "& .cwt-name": {
                        color: neutralForegroundRest
                    },

                    "& .cwt-reaction": {
                        margin: "0 10px",
                        "font-size": "16px"
                    },

                    "& .cwt-reaction-more": {
                        [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "5px",
                        [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "10px"
                    },

                    "& .cwt-fast-button": {
                        height: "21px",
                        "min-width": "64px",
                        [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "20px",
                        "font-size": "12px"
                    },

                    "& .float-item": {
                        display: "flex",
                        height: "36px",
                        "align-items": "center",

                        "& a.cwt-link.cwt-avatar-link": {
                            display: "flex"
                        }
                    }
                },

                "& li.cwt-rest-reactions": {
                    "& .cwt-name": {
                        color: neutralForegroundHint
                    }
                }
            }
        }
    }
});